import {TIME} from '@stubhub/general-utils';
import localStorage from '@stubhub/local-storage';
import {get} from '@stubhub/rest-method';

const FX_REQUEST_CACHE_TIME = 5 * TIME.SECONDS_OF_MIN;

/**
 * Retrieves exchange data for a given currency pair and path.
 *
 * @param {string} from - The currency to convert from.
 * @param {string} to - The currency to convert to.
 * @param {string} path - The path for the exchange data.
 * @returns {Promise} - A promise that resolves to the exchange data.
 */
const getExchangeData = (from, to, path) => {
  return get({
    host: process.env.REACT_APP_API_HOST,
    path: `/fx/exchange/${path}/daily?from=${from}&to=${to}`,
    headers: {
      Authorization: process.env.REACT_APP_API_SECRET,
    },
    json: true,
    cache: true,
    cacheMaxAge: FX_REQUEST_CACHE_TIME,
  });
};

/**
 * Retrieves the exchange rate between two currencies.
 *
 * @param {string} from - The currency to convert from.
 * @param {string} to - The currency to convert to.
 * @returns {Promise} A promise that resolves to the exchange rate.
 */
export const getExchangeRate = (from, to) => {
  return getExchangeData(from, to, 'v1');
};

/**
 * Retrieves the exchange rates from the API for the specified currencies.
 *
 * @param {string} from - The currency to convert from.
 * @param {string} to - The currency to convert to.
 * @returns {Promise} - A promise that resolves to the exchange rates.
 */
export const getExchangeRates = (from, to) => {
  return getExchangeData(to, from, 'v2');
};

/**
 * This function will make an api call to fetch the exchange rate and will store it in the local storage.
 * @param {String} from transactional currency
 * @param {String} to Destination advisory currency
 * @returns {Promise<number>} the exchange rate returned from the api call
 */
export const fetchCurrencyExchangeRate = async (from, to) => {
  const selectedCurrencyKey = `${from}_${to}`;
  const today = new Date().toLocaleDateString();
  let newExchangeRate = {};
  try {
    const exchangeRate = await getExchangeRate(from, to);
    newExchangeRate = {
      amount: exchangeRate.amount,
      date: today,
      error: false,
    };
  } catch (err) {
    newExchangeRate = {error: true};
  }
  const exchangeRates = getLocalStorageExchangeRates();
  exchangeRates[selectedCurrencyKey] = newExchangeRate;
  localStorage().setItem('exchangeRates', JSON.stringify(exchangeRates));

  return exchangeRates[selectedCurrencyKey];
};

/**
 * Makes an API call to retrieve all the currency exchanges and stores them in the local storage.
 * If the API call fails, it will store the currency with an error flag
 * @param {Array<String>} from Array of currencies to get exchange rate
 * @param {String} to Destination advisory currency
 * @returns An array of objects with exchange rates status
 */
export const fetchCurrencyExchangeRates = async (from, to) => {
  const exchangeRatesCache = getLocalStorageExchangeRates();
  try {
    const exchangeRates = await getExchangeRates(from, to);
    const today = new Date().toLocaleDateString();
    exchangeRates.forEach(({amount, from: exchangeFrom, to: exchangeTo}) => {
      // We need to store the inverse exchange rates
      const key = `${exchangeTo}_${exchangeFrom}`;
      const newExchangeRate = {
        amount: 1 / amount,
        date: today,
        error: false,
      };
      exchangeRatesCache[key] = newExchangeRate;
    });
  } catch {
    from?.forEach((exchangeFrom) => {
      const key = `${exchangeFrom}_${to}`;
      exchangeRatesCache[key] = {error: true};
    });
  }
  localStorage().setItem('exchangeRates', JSON.stringify(exchangeRatesCache));

  return exchangeRatesCache;
};

export const getLocalStorageExchangeRates = () => JSON.parse(localStorage().getItem('exchangeRates') || '{}') || {};
